/**
 * Repartir los WhatsApp de una campaña entre las líneas vinculadas por QR y WhatsApp oficial (la API).
 *
 * Pedido de Alan (12/10/2026): «poner una cantidad de mensajes que se envíen por el código QR y otra por la API»,
 * para probar las dos vías con la misma base. Cada persona va por una sola vía: las líneas le escriben a las de QR y
 * la campaña de WhatsApp oficial de este análisis, a las de la API (el backend lo aplica en los dos lados).
 *
 * Muestra cuántos pueden recibir por cada vía, deja elegir las cantidades y guarda (`PUT /campaigns/:id/reparto`).
 * Una vez que salió una parte, el backend lo deja fijo y acá se ve como quedó, sin poder cambiarlo.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, BadgeCheck, Loader2, Lock, QrCode, Split } from "lucide-react";
import { activaApi } from "../api";
import type { EstadoReparto } from "../types.real";
import { num } from "../utils/format";
import { leerCantidad, motivoNoAlcanza, resumenReparto, rutaParteApi } from "../utils/reparto";

interface Props {
  campaignId: string;
  /** el equipo reparte; una persona de un cliente sólo ve cómo quedó (el backend también se lo niega) */
  puedeEditar?: boolean;
  /** el estado después de leer, repartir o sacar el reparto */
  onCambio?: (e: EstadoReparto) => void;
  /** armar la parte de la API cuando todavía no hay campaña de WhatsApp oficial de este análisis (sin esto, no se ofrece) */
  onAbrirApi?: () => void;
  /** mientras quien llama arma la campaña oficial */
  abriendoApi?: boolean;
}

export function RepartoWhatsApp({ campaignId, puedeEditar = true, onCambio, onAbrirApi, abriendoApi = false }: Props) {
  const [estado, setEstado] = useState<EstadoReparto | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [qr, setQr] = useState("");
  const [api, setApi] = useState("");
  const [error, setError] = useState("");

  const adoptar = useCallback((e: EstadoReparto) => {
    setEstado(e);
    setQr(e.reparto ? String(e.reparto.qr) : "");
    setApi(e.reparto ? String(e.reparto.api) : "");
    onCambio?.(e);
  }, [onCambio]);

  useEffect(() => {
    let vivo = true;
    setCargando(true); setError("");
    activaApi.reparto(campaignId)
      .then((r: any) => {
        if (!vivo) return;
        if (r?.success === false || !r?.data) { setError(r?.error || "No pudimos leer el reparto."); return; }
        adoptar(r.data);
      })
      .catch((e: any) => { if (vivo) setError(e?.message || "No pudimos leer el reparto."); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
    // `adoptar` cambia con `onCambio`: se lee una vez por campaña, no cada vez que el padre se vuelve a dibujar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const nQr = leerCantidad(qr);
  const nApi = leerCantidad(api);
  const motivo = motivoNoAlcanza(nQr, nApi, estado?.disponibles);

  const guardar = async () => {
    if (motivo || nQr === null || nApi === null) { setError(motivo || ""); return; }
    setGuardando(true); setError("");
    try {
      const r = await activaApi.repartir(campaignId, nQr, nApi);
      // un 422 (no alcanza, ya salió una parte) vuelve sin lanzar, con el motivo del backend
      if (r?.success === false || !r?.data) { setError(r?.error || "No pudimos guardar el reparto. Probá de nuevo."); return; }
      adoptar(r.data);
      setEditando(false);
    } catch (e: any) {
      setError(e?.message || "No pudimos guardar el reparto. Probá de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  const sacar = async () => {
    setGuardando(true); setError("");
    try {
      const r = await activaApi.quitarReparto(campaignId);
      if (r?.success === false || !r?.data) { setError(r?.error || "No pudimos sacar el reparto. Probá de nuevo."); return; }
      adoptar(r.data);
      setEditando(false);
    } catch (e: any) {
      setError(e?.message || "No pudimos sacar el reparto. Probá de nuevo.");
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div className="rounded-2xl border border-slate-100 p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="reparto-cargando">
        <Loader2 size={14} className="animate-spin" /> Calculando a cuántos les llega por cada vía…
      </div>
    );
  }
  if (!estado) {
    return <p role="alert" className="text-sm text-red-700">{error || "No pudimos leer el reparto."}</p>;
  }

  const d = estado.disponibles;
  const r = estado.reparto;
  const rutaApi = rutaParteApi(estado.campanaOficial);
  const fijo = !!estado.bloqueo;
  const mostrarFormulario = puedeEditar && !fijo && (!r || editando);

  return (
    <div className="rounded-2xl border border-violet-100 bg-violet-50/40 p-4 space-y-3" data-testid="reparto-whatsapp">
      <div className="flex items-start gap-2.5">
        <Split size={17} className="text-violet-500 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Repartir los WhatsApp entre QR y API</h3>
          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
            Cada persona recibe por una sola vía. Las líneas vinculadas con el QR les escriben a las de QR, con el
            mensaje que arma Activa. WhatsApp oficial les escribe a las de la API, con una plantilla aprobada por Meta.
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
        <div className="rounded-xl bg-white border border-slate-100 px-3 py-2">
          <dt className="text-xs text-slate-500 flex items-center gap-1"><QrCode size={12} /> Pueden por QR</dt>
          <dd className="font-semibold text-slate-900" data-testid="reparto-disp-qr">{num(d.qr)}</dd>
        </div>
        <div className="rounded-xl bg-white border border-slate-100 px-3 py-2">
          <dt className="text-xs text-slate-500 flex items-center gap-1"><BadgeCheck size={12} /> Pueden por la API</dt>
          <dd className="font-semibold text-slate-900" data-testid="reparto-disp-api">{num(d.api)}</dd>
        </div>
        <div className="rounded-xl bg-white border border-slate-100 px-3 py-2">
          <dt className="text-xs text-slate-500">Personas distintas</dt>
          <dd className="font-semibold text-slate-900">{num(d.total)}</dd>
        </div>
      </dl>
      <p className="text-xs text-slate-500 leading-relaxed">
        Por la API sólo reciben quienes dijeron que sí y tienen un celular que acepta Meta. Primero se sortean los de la
        API y después los de QR, entre los que quedan. La fatiga y los límites de cada vía se descuentan al enviar.
      </p>

      {r && !mostrarFormulario && (
        <div className="rounded-xl bg-white border border-slate-200 px-3.5 py-2.5 flex flex-wrap items-center gap-2 text-sm" data-testid="reparto-guardado">
          {fijo ? <Lock size={14} className="text-slate-400" /> : <Split size={14} className="text-violet-500" />}
          <p className="flex-1 min-w-0 text-slate-700">
            Repartido: <strong className="text-slate-900">{resumenReparto(r)}</strong>
          </p>
          {puedeEditar && !fijo && (
            <>
              <button type="button" onClick={() => { setEditando(true); setError(""); }} className="text-xs font-semibold text-violet-600 hover:text-violet-800">
                Cambiar
              </button>
              <button type="button" onClick={() => void sacar()} disabled={guardando} className="text-xs font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-50">
                Sacar el reparto
              </button>
            </>
          )}
        </div>
      )}

      {fijo && <p className="text-xs text-slate-600 flex items-start gap-1.5"><Lock size={12} className="mt-0.5 flex-shrink-0" /> {estado.bloqueo}</p>}

      {mostrarFormulario && (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Por QR (líneas vinculadas)</span>
              <input
                inputMode="numeric"
                value={qr}
                onChange={(e) => setQr(e.target.value)}
                placeholder="0"
                aria-describedby="reparto-max-qr"
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <span id="reparto-max-qr" className="text-[11px] text-slate-400">hasta {num(d.qr)}</span>
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-700">Por la API (WhatsApp oficial)</span>
              <input
                inputMode="numeric"
                value={api}
                onChange={(e) => setApi(e.target.value)}
                placeholder="0"
                aria-describedby="reparto-max-api"
                className="mt-1 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <span id="reparto-max-api" className="text-[11px] text-slate-400">hasta {num(d.api)}</span>
            </label>
          </div>
          {motivo && (nQr !== 0 || nApi !== 0) && (
            <p className="text-xs text-amber-700 flex items-start gap-1.5"><AlertTriangle size={12} className="mt-0.5 flex-shrink-0" /> {motivo}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void guardar()}
              disabled={guardando || !!motivo}
              className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar el reparto
            </button>
            {r && (
              <button type="button" onClick={() => { setEditando(false); setQr(String(r.qr)); setApi(String(r.api)); setError(""); }} className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}

      {r && r.api > 0 && rutaApi && (
        <Link to={rutaApi} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900">
          Ver la parte de la API en WhatsApp oficial <ArrowRight size={12} />
        </Link>
      )}
      {/* todavía no hay campaña oficial de este análisis: se arma desde acá (lee sólo a los de la API) */}
      {r && r.api > 0 && !rutaApi && puedeEditar && onAbrirApi && (
        <button type="button" onClick={onAbrirApi} disabled={abriendoApi} className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900 disabled:opacity-50">
          {abriendoApi ? <Loader2 size={12} className="animate-spin" /> : <BadgeCheck size={12} />} Armar la parte de la API en WhatsApp oficial <ArrowRight size={12} />
        </button>
      )}

      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
